import {MutableRefObject, useCallback, useEffect, useState} from 'react';

export type HTMLElementOrNull = HTMLElement | null;
export type RefElementOrNull<T> = T | null;
export type CallbackRef = (node: HTMLElementOrNull) => any;
export type AnyRef = CallbackRef | MutableRefObject<HTMLElementOrNull>;

const config: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px 0px 0px 0px',
  threshold: [0, 1],
};

function useInViewRef(
  callback: IntersectionObserverCallback = () => {},
  options: IntersectionObserverInit = config,
): [CallbackRef, boolean] {
  const {root = null, rootMargin, threshold} = options;

  const [node, setNode] = useState<HTMLElementOrNull>(null);
  const [inView, setInView] = useState<boolean>(false);

  useEffect(() => {
    // Create an observer instance linked to the callback function
    if (node) {
      const observer = new IntersectionObserver((entries, observerRef) => {
        entries.forEach(({isIntersecting}) => setInView(isIntersecting));
        callback(entries, observerRef);
      }, options);

      // Start observing the target node for configured mutations
      observer.observe(node);
      return () => {
        observer.disconnect();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, callback, root, rootMargin, threshold]);

  const ref = useCallback((node: HTMLElementOrNull) => {
    setNode(node);
  }, []);

  return [ref, inView];
}

export {useInViewRef};
